import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  Link,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import dayjs from 'dayjs';
import { Helmet } from 'react-helmet-async';
import { loadStripe } from '@stripe/stripe-js';

import {
  downloadPublicInvoicePdf,
  createPublicAutopayConsent,
  retrievePublicInvoice,
  retrievePublicPaymentConfig,
  retrievePublicPaymentIntent,
  retrievePublicPaymentStatus,
  reportPublicPayment,
} from '@src/api/invoices';
import houseLogo from '@src/assets/logo-full.png';

export const formatCurrency = (value) => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  const amount = Number(value);
  if (Number.isNaN(amount)) {
    return String(value);
  }
  return amount.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  });
};

export const formatDate = (value) => (value ? dayjs(value).format('MMM D, YYYY') : '—');

export const sanitizeLine = (value) => {
  if (value === undefined || value === null) {
    return null;
  }
  const stringValue = String(value).trim();
  return stringValue.length ? stringValue : null;
};

export const dedupeLines = (lines) => {
  const seen = new Set();
  return lines.filter((line) => {
    const sanitized = sanitizeLine(line);
    if (!sanitized) {
      return false;
    }
    if (seen.has(sanitized)) {
      return false;
    }
    seen.add(sanitized);
    return true;
  });
};

export const buildAddressLines = (address) => {
  if (!address || typeof address !== 'object') {
    return [];
  }
  if (Array.isArray(address.lines)) {
    return dedupeLines(address.lines.map(sanitizeLine));
  }
  const line1 = sanitizeLine(address.line1 || address.address1 || address.address_line1);
  const line2 = sanitizeLine(address.line2 || address.address2 || address.address_line2);
  const city = sanitizeLine(address.city);
  const state = sanitizeLine(address.state || address.region);
  const postal = sanitizeLine(address.postal_code || address.postalCode || address.zip);
  const country = sanitizeLine(address.country);
  const lines = [line1, line2];
  const cityLineParts = [city, state, postal].filter(Boolean);
  if (cityLineParts.length) {
    lines.push(cityLineParts.join(', '));
  }
  lines.push(country);
  return dedupeLines(lines);
};

export const buildCompanyLines = (snapshot, defaults = []) => {
  if (!snapshot || typeof snapshot !== 'object') {
    return defaults;
  }
  if (Array.isArray(snapshot.lines)) {
    const prepared = dedupeLines(snapshot.lines.map(sanitizeLine));
    if (prepared.length) {
      return prepared;
    }
  }
  const name = sanitizeLine(snapshot.name || snapshot.company || snapshot.company_name);
  const addressLines = [
    ...buildAddressLines(snapshot),
    ...buildAddressLines(snapshot.address),
  ];
  const extras = [
    sanitizeLine(snapshot.email),
    sanitizeLine(snapshot.phone),
    sanitizeLine(snapshot.website || snapshot.url),
  ];
  const combined = dedupeLines([name, ...addressLines, ...extras]);
  return combined.length ? combined : defaults;
};

export const DEFAULT_FROM_LINES = [
  'Lead Gurus',
  '1225 Ave. Ponce de León',
  'PH-881',
  'San Juan, PR 00907',
  'United States of America',
  'billing@leadgurus.com',
];

export const parseAmount = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const numberValue = Number(value);
  if (Number.isNaN(numberValue)) {
    return null;
  }
  return numberValue;
};

export const amountToCents = (value) => {
  const amount = parseAmount(value);
  if (amount == null) {
    return null;
  }
  return Math.round(amount * 100);
};

export const normalizePaymentOptions = (invoice) => {
  const snapshot =
    invoice?.payment_options_snapshot ||
    invoice?.processing_fee_snapshot ||
    invoice?.stripe_payment_options_snapshot ||
    invoice?.payment_option_snapshot;

  if (!snapshot) {
    return [];
  }

  const entries = Array.isArray(snapshot)
    ? snapshot.map((entry) => [entry?.type || entry?.payment_method, entry])
    : Object.entries(snapshot);

  return entries
    .map(([key, raw]) => {
      if (!raw || typeof raw !== 'object') {
        return null;
      }

      const type = raw.type || raw.payment_method || raw.paymentMethod || key;
      if (!type) {
        return null;
      }

      const totalRaw =
        raw.total_with_fee ||
        raw.total_amount ||
        raw.amount_with_fee ||
        raw.total ||
        raw.totalDue ||
        raw.amount;
      const feeAmountRaw =
        raw.processing_fee_amount ||
        raw.processing_fee ||
        raw.fee_amount ||
        raw.fee ||
        raw.total_fee;
      const feePercentRaw =
        raw.processing_fee_percent ||
        raw.fee_percent ||
        raw.percent_fee ||
        raw.percent;
      const feeFlatRaw =
        raw.processing_fee_flat_amount ||
        raw.processing_fee_flat ||
        raw.fee_flat_amount ||
        raw.fee_flat ||
        raw.flat_fee;

      return {
        type,
        label: raw.label || raw.name || raw.display_name || raw.displayName || type,
        totalWithFee: parseAmount(totalRaw),
        totalWithFeeRaw: totalRaw,
        feeAmount: parseAmount(feeAmountRaw),
        feeAmountRaw,
        feePercent: parseAmount(feePercentRaw),
        feePercentRaw,
        feeFlat: parseAmount(feeFlatRaw),
        feeFlatRaw,
      };
    })
    .filter(Boolean);
};

export const getPaymentActionGuidance = (result) => {
  const status = result?.paymentIntent?.status || '';
  const nextActionType = result?.paymentIntent?.next_action?.type || '';
  const errorCode = String(result?.error?.code || '').toLowerCase();
  const errorMessage = String(result?.error?.message || '').toLowerCase();
  const hasMicrodepositHint =
    errorCode.includes('microdeposit') || errorMessage.includes('microdeposit');

  if (status === 'requires_action') {
    if (nextActionType === 'verify_with_microdeposits' || hasMicrodepositHint) {
      return (
        'Additional bank verification is required. Stripe sent two microdeposits to the bank ' +
        'account. Verify those amounts in Stripe to complete payment.'
      );
    }
    if (nextActionType === 'redirect_to_url' || nextActionType === 'use_stripe_sdk') {
      return (
        'Additional bank authentication is required. Follow the Stripe prompt to finish ' +
        'verification and submit payment.'
      );
    }
    return (
      'Additional verification is required by your bank. Follow the Stripe prompt to complete ' +
      'payment, then retry if needed.'
    );
  }

  if (hasMicrodepositHint) {
    return (
      'Bank verification via microdeposits is required before this ACH payment can complete. ' +
      'Please verify the deposited amounts in Stripe and submit payment again.'
    );
  }

  return null;
};

export const getUnexpectedPaymentErrorMessage = (error) => {
  const reason = String(error?.message || '').trim();
  const code = String(error?.code || '').trim();
  if (reason && code) {
    return `Payment could not be confirmed: ${reason} (${code}).`;
  }
  if (reason) {
    return `Payment could not be confirmed: ${reason}`;
  }
  if (code) {
    return `Payment could not be confirmed (${code}).`;
  }
  return 'Unexpected error confirming payment.';
};

export const getPersistedPaymentFailureMessage = (invoice) => {
  if (!invoice || typeof invoice !== 'object') {
    return '';
  }
  const rawMessage = String(invoice.payment_failure_message || '').trim();
  if (rawMessage) {
    return rawMessage;
  }
  if (invoice.payment_failed_at) {
    return 'A previous bank payment attempt failed. Please verify account details and try again.';
  }
  return '';
};

export const describePaymentStatusForClient = (intentStatus) => {
  switch (String(intentStatus || '').trim().toLowerCase()) {
    case 'processing':
      return 'Your payment is on its way. Bank payments usually clear in 1–2 business days.';
    case 'succeeded':
      return "Payment received. We'll email you a receipt shortly.";
    case 'requires_action':
      return 'Your bank needs one more step before this payment can complete.';
    case 'requires_payment_method':
    case 'canceled':
      return 'That payment did not go through. Please try again with another method.';
    case 'requires_confirmation':
      return 'Your payment is ready to submit.';
    default:
      return 'We are still waiting on a result from your bank.';
  }
};

export const isMicrodepositVerificationMessage = (message) =>
  /micro[\s-]?deposit/i.test(String(message || ''));

export const MANUAL_ACH_HELPER_TEXT =
  'Bank login is usually faster. Manual bank entry may require additional bank verification.';
export const AUTOPAY_CONSENT_SUMMARY =
  'Authorize Lead Gurus to save this payment method and automatically charge future invoices for this billing account.';
export const ACH_RECOMMENDED_TEXT =
  'Recommended for future invoice payments. ACH helps avoid card processing fees, expired cards, and billing delays.';

export function PaymentSummary({ baseAmount, baseAmountRaw, selectedOption, options, onOptionSelect }) {
  const displayBase = formatCurrency(baseAmountRaw ?? baseAmount);
  const totalAmount = selectedOption?.totalWithFee ?? baseAmount;
  const displayTotal = formatCurrency(
    selectedOption?.totalWithFeeRaw ?? totalAmount ?? baseAmountRaw ?? baseAmount,
  );
  const feeAmount =
    selectedOption?.feeAmount != null
      ? formatCurrency(selectedOption.feeAmount)
      : selectedOption?.feeAmountRaw
        ? formatCurrency(selectedOption.feeAmountRaw)
        : null;

  const percentLabel = (() => {
    if (!selectedOption?.feePercentRaw && selectedOption?.feePercent == null) {
      return '';
    }
    if (selectedOption?.feePercentRaw) {
      return `${selectedOption.feePercentRaw}%`;
    }
    const percent = selectedOption?.feePercent;
    if (percent == null) {
      return '';
    }
    return `${percent.toFixed(2).replace(/\.00$/, '')}%`;
  })();

  const flatFeeLabel = (() => {
    if (!selectedOption) {
      return '';
    }
    if (selectedOption.feeFlatRaw) {
      return formatCurrency(selectedOption.feeFlatRaw);
    }
    if (selectedOption.feeFlat != null) {
      return formatCurrency(selectedOption.feeFlat);
    }
    return '';
  })();

  return (
    <Stack spacing={2} sx={{ mt: 2 }}>
      <Box>
        <Typography variant="subtitle2" color="text.secondary">
          Invoice total
        </Typography>
        <Typography variant="body1">{displayBase}</Typography>
      </Box>
      {options.length ? (
        <Stack spacing={1}>
	          {options.map((option) => {
	            const optionTotal = option.totalWithFee ?? baseAmount;
	            const optionDisplayTotal = formatCurrency(
	              option.totalWithFeeRaw ?? optionTotal ?? baseAmountRaw ?? baseAmount,
	            );
	            const isSelected = option.type === selectedOption?.type;
	            const isRecommendedBankOption = option.type === 'us_bank_account';
            const optionFee =
              option.feeAmount != null
                ? formatCurrency(option.feeAmount)
                : option.feeAmountRaw
                  ? formatCurrency(option.feeAmountRaw)
                  : '—';
            const optionPercentLabel = (() => {
              if (option.feePercentRaw) {
                return `${option.feePercentRaw}%`;
              }
              if (option.feePercent == null) {
                return '';
              }
              return `${option.feePercent.toFixed(2).replace(/\.00$/, '')}%`;
            })();
            const optionFlatLabel = (() => {
              if (option.feeFlatRaw) {
                return formatCurrency(option.feeFlatRaw);
              }
              if (option.feeFlat != null) {
                return formatCurrency(option.feeFlat);
              }
              return '';
            })();

            return (
              <Box
                key={option.type}
                data-testid={`payment-option-${option.type}`}
                sx={{
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: isSelected ? 'primary.main' : 'divider',
                  bgcolor: isSelected ? 'action.hover' : 'transparent',
                  px: 2,
                  py: 1.5,
                  cursor: onOptionSelect ? 'pointer' : 'default',
                }}
                onClick={onOptionSelect ? () => onOptionSelect(option.type) : undefined}
                role={onOptionSelect ? 'button' : undefined}
                tabIndex={onOptionSelect ? 0 : undefined}
                onKeyDown={
                  onOptionSelect
                    ? (event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          onOptionSelect(option.type);
                        }
                      }
                    : undefined
                }
              >
	                <Stack spacing={0.5}>
	                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
	                    {option.label}{' '}
	                    {isRecommendedBankOption ? (
	                      <Typography component="span" variant="caption" color="success.main">
	                        (Recommended)
	                      </Typography>
	                    ) : null}{' '}
	                    {isSelected ? (
	                      <Typography component="span" variant="caption" color="primary">
	                        (Selected)
	                      </Typography>
	                    ) : null}
	                  </Typography>
	                  {isRecommendedBankOption ? (
	                    <Typography variant="caption" color="success.main">
	                      Best for automatic future invoice payments.
	                    </Typography>
	                  ) : null}
	                  <Typography variant="body2" color="text.secondary">
	                    Processing fee: {optionFee}
                    {optionPercentLabel ? ` (${optionPercentLabel}${optionFlatLabel ? ` + ${optionFlatLabel}` : ''})` : ''}
                    {!optionPercentLabel && optionFlatLabel ? ` (${optionFlatLabel})` : ''}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    Total: {optionDisplayTotal}
                  </Typography>
                </Stack>
              </Box>
            );
          })}
        </Stack>
      ) : null}
      <Box>
        <Typography variant="subtitle2" color="text.secondary">
          Amount to be charged
        </Typography>
        <Typography variant="h5" color="primary" sx={{ fontWeight: 700 }}>
          {displayTotal}
        </Typography>
        {feeAmount && (percentLabel || flatFeeLabel) ? (
          <Typography variant="caption" color="text.secondary">
            Includes processing fee {feeAmount}
            {percentLabel ? ` (${percentLabel}${flatFeeLabel ? ` + ${flatFeeLabel}` : ''})` : ''}
            {!percentLabel && flatFeeLabel ? ` (${flatFeeLabel})` : ''}
          </Typography>
        ) : feeAmount ? (
          <Typography variant="caption" color="text.secondary">
            Includes processing fee {feeAmount}
          </Typography>
        ) : null}
      </Box>
    </Stack>
  );
}

export function PaymentFormContent({
  number,
  token,
  invoiceTotal,
  invoiceTotalRaw,
  options,
  selectedOption,
  onSelectionChange,
  currentSelection,
  selectionDisabled,
  onSuccess,
  onError,
  onMicrodepositVerificationUrl,
  createPaymentIntent,
  submitAutopayConsent,
  autopayConsentChecked,
  autopayConsentText,
  autopayConsentSubmitting,
  autopayAlreadyAuthorized,
  onAutopayConsentChange,
  onAutopayConsentSaved,
  setAutopayConsentSubmitting,
  submitting,
  setSubmitting,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [elementComplete, setElementComplete] = useState(false);
  const [elementReady, setElementReady] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements) {
      return;
    }
    if (!elementReady || !elements.getElement(PaymentElement)) {
      onError('Payment form is still loading. Please wait a moment and try again.');
      return;
    }
    onError('');
    onSuccess('');
    setSubmitting(true);
    try {
      const submitResult = await elements.submit();
      if (submitResult?.error) {
        onError(submitResult.error.message || 'Payment details are incomplete.');
        return;
      }
      if (
        autopayConsentChecked &&
        !autopayAlreadyAuthorized &&
        !autopayConsentSubmitting
      ) {
        setAutopayConsentSubmitting(true);
        const defaultSubmit = () =>
          createPublicAutopayConsent({
            number,
            token,
            payment_method: currentSelection,
          });
        const consentResponse = await (submitAutopayConsent
          ? submitAutopayConsent(currentSelection)
          : defaultSubmit());
        onAutopayConsentSaved(consentResponse?.data?.consent || null);
      }
      const paymentIntent = await createPaymentIntent(currentSelection);
      if (!paymentIntent?.clientSecret) {
        onError('We could not initialize the payment. Please try again.');
        return;
      }
      const result = await stripe.confirmPayment({
        elements,
        clientSecret: paymentIntent.clientSecret,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: 'if_required',
      });

      if (result.error) {
        const verificationUrl =
          result?.paymentIntent?.next_action?.verify_with_microdeposits?.hosted_verification_url ||
          '';
        onMicrodepositVerificationUrl(verificationUrl);
        const guidance = getPaymentActionGuidance(result);
        onError(guidance || result.error.message || 'Payment could not be completed.');
        setSubmitting(false);
        return;
      }

      const status = result.paymentIntent?.status;
      if (status && status !== 'succeeded' && status !== 'processing') {
        const verificationUrl =
          result?.paymentIntent?.next_action?.verify_with_microdeposits?.hosted_verification_url ||
          '';
        onMicrodepositVerificationUrl(verificationUrl);
        const guidance = getPaymentActionGuidance(result);
        onError(guidance || `Payment status: ${status}`);
        setSubmitting(false);
        return;
      }

      onSuccess(
        status === 'processing'
          ? 'Payment submitted. Stripe is still processing your payment.'
          : "Payment confirmed! We'll email you a receipt shortly.",
        status || '',
      );
      onMicrodepositVerificationUrl('');
    } catch (err) {
      console.error('Unexpected payment error', err);
      setAutopayConsentSubmitting(false);
      onMicrodepositVerificationUrl('');
      onError(getUnexpectedPaymentErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleElementChange = (event) => {
    if (typeof event?.complete === 'boolean') {
      setElementComplete(event.complete);
    }
    const methodType = event?.value?.type;
    if (methodType && !selectionDisabled && methodType !== currentSelection) {
      onSelectionChange(methodType);
    }
  };

  const order = selectedOption?.type
    ? [selectedOption.type]
    : options.length
      ? options.map((option) => option.type)
      : undefined;
  const totalForButton =
    selectedOption?.totalWithFeeRaw ??
    selectedOption?.totalWithFee ??
    invoiceTotal ??
    invoiceTotalRaw;
  const buttonLabel = `Pay ${formatCurrency(totalForButton)} securely`;

  return (
    <form onSubmit={handleSubmit}>
      <Stack spacing={3}>
        <PaymentElement
          onReady={() => setElementReady(true)}
          onChange={handleElementChange}
        />
        {currentSelection === 'us_bank_account' ? (
          <Typography variant="caption" color="text.secondary">
            {MANUAL_ACH_HELPER_TEXT}
          </Typography>
        ) : null}
        {autopayAlreadyAuthorized ? (
          <Alert severity="success" sx={{ borderRadius: 2 }}>
            Future auto-charge is already authorized for this billing account.
          </Alert>
        ) : (
          <Stack spacing={1}>
            <FormControlLabel
              control={(
                <Checkbox
                  checked={autopayConsentChecked}
                  disabled={submitting || autopayConsentSubmitting}
                  onChange={(event) => onAutopayConsentChange(event.target.checked)}
                />
              )}
              label={(
                <Typography variant="body2">
                  {AUTOPAY_CONSENT_SUMMARY}
                </Typography>
              )}
              sx={{ alignItems: 'flex-start', m: 0 }}
            />
            <Box sx={{ pl: 4.5 }}>
              <Link
                component="button"
                type="button"
                variant="body2"
                underline="hover"
                onClick={() => onAutopayConsentChange('open_details')}
              >
                Review authorization details
              </Link>
            </Box>
          </Stack>
        )}
        <PaymentSummary
          baseAmount={invoiceTotal}
          baseAmountRaw={invoiceTotalRaw}
          selectedOption={selectedOption}
          options={options}
          onOptionSelect={onSelectionChange}
        />
        <Box display="flex" justifyContent="flex-end">
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={!stripe || !elements || submitting || !elementComplete || !elementReady}
            sx={{ borderRadius: 9999 }}
          >
            {submitting ? 'Processing…' : buttonLabel}
          </Button>
        </Box>
      </Stack>
    </form>
  );
}

export function PaymentForm({
  customerSessionClientSecret,
  stripePromise,
  currency,
  paymentMethodOptions,
  number,
  token,
  invoiceTotal,
  invoiceTotalRaw,
  options,
  selectedOption,
  onSelectionChange,
  selectionDisabled,
  onSuccess,
  onError,
  onMicrodepositVerificationUrl,
  createPaymentIntent,
  autopayConsentChecked,
  autopayConsentText,
  autopayConsentSubmitting,
  autopayAlreadyAuthorized,
  onAutopayConsentChange,
  onAutopayConsentSaved,
  setAutopayConsentSubmitting,
  submitting,
  setSubmitting,
}) {
  if (!selectedOption?.type || !stripePromise) {
    return null;
  }

  const shouldSavePaymentMethod = autopayAlreadyAuthorized || autopayConsentChecked;
  const selectedAmount =
    selectedOption?.totalWithFeeRaw ??
    selectedOption?.totalWithFee ??
    invoiceTotalRaw ??
    invoiceTotal;
  const amount = amountToCents(selectedAmount);
  if (amount == null) {
    return null;
  }
  const elementsKey = [
    selectedOption.type,
    amount,
    shouldSavePaymentMethod ? 'save' : 'nosave',
  ].join('-');
  const elementPaymentMethodOptions =
    selectedOption.type === 'us_bank_account'
      ? {
          us_bank_account: {
            ...(paymentMethodOptions?.us_bank_account || {}),
          },
        }
      : undefined;

  return (
    <Elements
      key={elementsKey}
      stripe={stripePromise}
      options={{
        mode: 'payment',
        currency: currency || 'usd',
        amount,
        paymentMethodTypes: [selectedOption.type],
        ...(shouldSavePaymentMethod ? { setupFutureUsage: 'off_session' } : {}),
        ...(customerSessionClientSecret
          ? { customerSessionClientSecret }
          : {}),
        ...(elementPaymentMethodOptions
          ? { paymentMethodOptions: elementPaymentMethodOptions }
          : {}),
        appearance: {
          theme: 'stripe',
          labels: 'floating',
        },
      }}
    >
      <PaymentFormContent
        number={number}
        token={token}
        invoiceTotal={invoiceTotal}
        invoiceTotalRaw={invoiceTotalRaw}
        options={options}
        selectedOption={selectedOption}
        onSelectionChange={onSelectionChange}
        currentSelection={selectedOption?.type}
        selectionDisabled={selectionDisabled}
        onSuccess={onSuccess}
        onError={onError}
        onMicrodepositVerificationUrl={onMicrodepositVerificationUrl}
        createPaymentIntent={createPaymentIntent}
        autopayConsentChecked={autopayConsentChecked}
        autopayConsentText={autopayConsentText}
        autopayConsentSubmitting={autopayConsentSubmitting}
        autopayAlreadyAuthorized={autopayAlreadyAuthorized}
        onAutopayConsentChange={onAutopayConsentChange}
        onAutopayConsentSaved={onAutopayConsentSaved}
        setAutopayConsentSubmitting={setAutopayConsentSubmitting}
        submitting={submitting}
        setSubmitting={setSubmitting}
      />
    </Elements>
  );
}

export default function PublicInvoicePage() {
  const { number } = useParams();
  const [searchParams] = useSearchParams();
  const rawToken = searchParams.get('token');
  const token = useMemo(
    () => (rawToken ? rawToken.replace(/\s/g, '+') : rawToken),
    [rawToken],
  );

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [invoice, setInvoice] = useState(null);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [downloadError, setDownloadError] = useState('');
  const [paymentIntentLoading, setPaymentIntentLoading] = useState(false);
  const [paymentIntentError, setPaymentIntentError] = useState('');
  const [paymentSuccess, setPaymentSuccess] = useState('');
  const [paymentError, setPaymentError] = useState('');
  const [paymentSubmissionStatus, setPaymentSubmissionStatus] = useState('');
  const [paymentIntentRefreshNonce, setPaymentIntentRefreshNonce] = useState(0);
  const [customerSessionClientSecret, setCustomerSessionClientSecret] = useState('');
  const [stripeCurrency, setStripeCurrency] = useState('usd');
  const [stripePaymentMethodOptions, setStripePaymentMethodOptions] = useState({});
  const [microdepositVerificationUrl, setMicrodepositVerificationUrl] = useState('');
  const [stripePromise, setStripePromise] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [autopayConsentChecked, setAutopayConsentChecked] = useState(false);
  const [autopayConsentSubmitting, setAutopayConsentSubmitting] = useState(false);
  const [paymentSubmitting, setPaymentSubmitting] = useState(false);
  const [paymentStatusRefreshing, setPaymentStatusRefreshing] = useState(false);
  const [achDialogOpen, setAchDialogOpen] = useState(false);
  // "I've already sent payment" — for money moving outside Stripe (direct
  // ACH, wire, check), which the app cannot see until someone records it.
  // Saying so here replaces the email clients currently have to write, and
  // stops the reminders while it settles.
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportForm, setReportForm] = useState({
    payment_method: 'ach_wire',
    sent_on: dayjs().format('YYYY-MM-DD'),
    reference: '',
  });
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [reportError, setReportError] = useState('');
  const [reportedAt, setReportedAt] = useState(null);
  const [autopayConsentDialogOpen, setAutopayConsentDialogOpen] = useState(false);
  const paymentIntentRequestRef = useRef(0);

  const isPaid = useMemo(
    () => (invoice?.status ? String(invoice.status).toLowerCase() === 'paid' : false),
    [invoice],
  );
  const paymentComplete = isPaid || paymentSubmissionStatus === 'succeeded';
  const autopayConsent = invoice?.autopay_consent || {};
  const autopayConsentText = invoice?.autopay_consent_text || 'Authorize future auto-charge.';
  const autopayConsentByMethod =
    autopayConsent && typeof autopayConsent.by_payment_method === 'object'
      ? autopayConsent.by_payment_method
      : {};
  const autopaySelectedMethodConsent = selectedPaymentMethod
    ? autopayConsentByMethod[selectedPaymentMethod] || null
    : null;
  const autopayAlreadyAuthorized = Boolean(autopaySelectedMethodConsent?.authorized);
  const persistedPaymentFailureMessage = useMemo(
    () => getPersistedPaymentFailureMessage(invoice),
    [invoice],
  );
  const hasMicrodepositVerificationState = useMemo(
    () =>
      [paymentError, paymentIntentError, persistedPaymentFailureMessage].some(
        isMicrodepositVerificationMessage,
      ),
    [paymentError, paymentIntentError, persistedPaymentFailureMessage],
  );

  useEffect(() => {
    if (!number) {
      setError('Missing invoice number.');
      setLoading(false);
      return;
    }
    if (!rawToken) {
      setError('Access token required to view this invoice.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    setDownloadError('');
    setDownloadLoading(false);
    setPaymentIntentError('');
    setPaymentSuccess('');
    setPaymentError('');
    setPaymentSubmissionStatus('');
    setCustomerSessionClientSecret('');
    setStripeCurrency('usd');
    setStripePaymentMethodOptions({});
    setMicrodepositVerificationUrl('');
    setSelectedPaymentMethod('');
    setAutopayConsentChecked(false);
    setAutopayConsentSubmitting(false);
    retrievePublicInvoice({ number, token })
      .then((res) => {
        setInvoice(res.data);
        // A payment already settling must survive a reload or a return
        // visit — otherwise the pay form reappears and invites a second
        // debit while the first ACH is still clearing.
        if (res.data?.payment_in_flight) {
          setPaymentSubmissionStatus('processing');
          setPaymentSuccess(describePaymentStatusForClient('processing'));
        }
      })
      .catch((err) => {
        console.error('Unable to load public invoice', err);
        const detail = err?.response?.data?.detail;
        setError(detail || 'We could not verify this invoice link.');
      })
      .finally(() => setLoading(false));
  }, [number, rawToken, token]);

  useEffect(() => {
    if (
      !invoice ||
      !number ||
      !token ||
      isPaid ||
      paymentComplete
    ) {
      return;
    }
    paymentIntentRequestRef.current += 1;
    const requestId = paymentIntentRequestRef.current;
    setPaymentIntentLoading(true);
    setPaymentIntentError('');
    const timer = setTimeout(() => {
      retrievePublicPaymentConfig({ number, token })
        .then((res) => {
          if (requestId !== paymentIntentRequestRef.current) {
            return;
          }
          const data = res.data || {};
          if (data.publishable_key) {
            setStripePromise((prev) => prev || loadStripe(data.publishable_key));
          }
          setCustomerSessionClientSecret(data.customer_session_client_secret || '');
          setStripeCurrency(data.currency || 'usd');
          setStripePaymentMethodOptions(data.payment_method_options || {});
          setMicrodepositVerificationUrl(data.microdeposit_verification_url || '');
        })
        .catch((err) => {
          console.error('Unable to initialize payment configuration', err);
          const detail = err?.response?.data?.detail;
          setPaymentIntentError(detail || 'We could not initialize the payment form.');
        })
        .finally(() => setPaymentIntentLoading(false));
    }, 150);
    return () => clearTimeout(timer);
  }, [
    invoice?.number,
    number,
    token,
    isPaid,
    paymentComplete,
    paymentIntentRefreshNonce,
  ]);

  useEffect(() => {
    if (!invoice || !number || !token || isPaid || paymentSubmissionStatus !== 'processing') {
      return undefined;
    }
    const interval = setInterval(() => {
      retrievePublicInvoice({ number, token })
        .then((res) => {
          const latestInvoice = res.data || null;
          setInvoice(latestInvoice);
          const hasFailure =
            latestInvoice &&
            (String(latestInvoice.payment_failure_message || '').trim() ||
              latestInvoice.payment_failed_at);
          if (hasFailure) {
            setPaymentSubmissionStatus('');
            setPaymentSuccess('');
          }
          if (latestInvoice?.status && String(latestInvoice.status).toLowerCase() === 'paid') {
            setPaymentSubmissionStatus('succeeded');
            setPaymentSuccess("Payment confirmed! We'll email you a receipt shortly.");
          }
        })
        .catch((err) => {
          console.error('Unable to refresh invoice payment status', err);
        });
    }, 8000);
    return () => clearInterval(interval);
  }, [invoice, isPaid, number, paymentSubmissionStatus, token]);

  const handleRefreshPaymentStatus = () => {
    if (!number || !token) {
      return;
    }
    setPaymentStatusRefreshing(true);
    retrievePublicPaymentStatus({ number, token })
      .then((res) => {
        const data = res.data || {};
        const latestInvoice = data.invoice || null;
        if (latestInvoice) {
          setInvoice(latestInvoice);
          if (latestInvoice?.status && String(latestInvoice.status).toLowerCase() === 'paid') {
            setPaymentSubmissionStatus('succeeded');
            setPaymentSuccess("Payment confirmed! We'll email you a receipt shortly.");
            setPaymentError('');
            setPaymentIntentError('');
            return;
          }
        }
        if (data.microdeposit_verification_url) {
          setMicrodepositVerificationUrl(data.microdeposit_verification_url);
        }
        const intentStatus = String(data.payment_intent_status || '').trim().toLowerCase();
        if (intentStatus) {
          // Stripe's raw intent status ("requires_action",
          // "requires_payment_method") was being shown to the client as-is.
          setPaymentSuccess(describePaymentStatusForClient(intentStatus));
          setPaymentSubmissionStatus(intentStatus === 'processing' ? 'processing' : '');
          setPaymentError('');
          setPaymentIntentError('');
        }
      })
      .catch((err) => {
        console.error('Unable to refresh payment status', err);
        const detail = err?.response?.data?.detail;
        setPaymentError(detail || 'Unable to refresh payment status right now.');
      })
      .finally(() => setPaymentStatusRefreshing(false));
  };

  const handleReportPayment = () => {
    setReportSubmitting(true);
    setReportError('');
    reportPublicPayment({ number, token, ...reportForm })
      .then((res) => {
        setReportedAt(res?.data?.expires_on || true);
        setReportDialogOpen(false);
      })
      .catch((err) => {
        console.error('Unable to report payment', err);
        setReportError(
          err?.response?.data?.detail ||
            err?.response?.data?.sent_on?.[0] ||
            'We could not record that. Please try again.',
        );
      })
      .finally(() => setReportSubmitting(false));
  };

  const handleDownloadPdf = () => {
    if (!invoice) {
      return;
    }
    setDownloadLoading(true);
    setDownloadError('');
    downloadPublicInvoicePdf({ number, token })
      .then((blob) => {
        const fileName = `${invoice.number || number}.pdf`;
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(blobUrl);
      })
      .catch((err) => {
        console.error('Unable to download public invoice PDF', err);
        const detail = err?.response?.data?.detail;
        setDownloadError(detail || 'We could not download the invoice PDF.');
      })
      .finally(() => setDownloadLoading(false));
  };

  const handleDownloadTerritoryCsv = () => {
    if (!invoice?.territory_breakdown?.length) {
      return;
    }
    const safeValue = (value) => {
      if (value === null || value === undefined) {
        return '';
      }
      const stringValue = String(value);
      if (stringValue.includes('"') || stringValue.includes(',') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    };
    const formatCsvDate = (value) => {
      if (!value) {
        return '';
      }
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return '';
      }
      return date.toISOString().slice(0, 10);
    };
    const periodStart = formatCsvDate(invoice.period_start);
    const periodEnd = formatCsvDate(invoice.period_end);
    const feeOnly = invoice.territory_breakdown_mode === 'fee_only';
    const rows = feeOnly
      ? [
          ['Territory', 'Vertical', 'Period start', 'Period end', 'Ad spend (paid directly by you)', 'Lead Gurus fee', 'Total'],
          ...invoice.territory_breakdown.map((row) => [
            safeValue(row.territory || ''),
            safeValue(row.vertical || ''),
            safeValue(periodStart),
            safeValue(periodEnd),
            safeValue(row.base_spend ?? ''),
            safeValue(row.spend || '0.00'),
            safeValue(row.total_cost ?? row.spend ?? ''),
          ]),
        ]
      : [
          ['Territory', 'Vertical', 'Period start', 'Period end', 'Spend'],
          ...invoice.territory_breakdown.map((row) => [
            safeValue(row.territory || ''),
            safeValue(row.vertical || 'Unassigned'),
            safeValue(periodStart),
            safeValue(periodEnd),
            safeValue(row.spend || '0.00'),
          ]),
        ];
    if (invoice.territory_breakdown_total) {
      rows.push([
        'Total',
        '',
        safeValue(periodStart),
        safeValue(periodEnd),
        ...(feeOnly ? [safeValue(invoice.territory_ad_spend_total || '')] : []),
        safeValue(invoice.territory_breakdown_total),
        ...(feeOnly ? [safeValue(invoice.territory_total_cost || '')] : []),
      ]);
    }
    const csvContent = rows.map((row) => row.map(safeValue).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const fileName = `${invoice.number || number}-territory-vertical-breakdown.csv`;
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
  };

  const handleDownloadClientBreakdownCsv = () => {
    if (!clientBreakdown.length || !invoice) {
      return;
    }
    const safeValue = (value) => {
      if (value === null || value === undefined) {
        return '';
      }
      const stringValue = String(value);
      if (stringValue.includes('"') || stringValue.includes(',') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    };
    const formatCsvDate = (value) => {
      if (!value) {
        return '';
      }
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return '';
      }
      return date.toISOString().slice(0, 10);
    };
    const periodStart = formatCsvDate(invoice.period_start);
    const periodEnd = formatCsvDate(invoice.period_end);
    const rows = [
      ['Client', 'Period start', 'Period end', 'Spend', 'Fee', 'Line total'],
      ...clientBreakdown.map((row) => [
        safeValue(row.client_name || ''),
        safeValue(periodStart),
        safeValue(periodEnd),
        safeValue(row.spend_amount || '0.00'),
        safeValue(row.fee_amount || '0.00'),
        safeValue(row.line_total || '0.00'),
      ]),
    ];
    rows.push([
      'Total',
      safeValue(periodStart),
      safeValue(periodEnd),
      '',
      '',
      safeValue(invoice.total_amount || '0.00'),
    ]);
    const csvContent = rows.map((row) => row.map(safeValue).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const fileName = `${invoice.number || number}-client-breakdown.csv`;
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
  };

  const achLines = useMemo(() => {
    if (!invoice?.ach_instructions) {
      return [];
    }
    return invoice.ach_instructions.split('\n').filter((line) => line.trim());
  }, [invoice]);

  const pageTitle = invoice
    ? `Invoice ${invoice.number} · Lead Gurus`
    : 'Invoice · Lead Gurus';

  const fromLines = useMemo(
    () => buildCompanyLines(invoice?.billing_from_snapshot, DEFAULT_FROM_LINES),
    [invoice],
  );

  const paymentOptions = useMemo(() => normalizePaymentOptions(invoice), [invoice]);

  useEffect(() => {
    if (selectedPaymentMethod) {
      return;
    }
    if (paymentOptions.length) {
      setSelectedPaymentMethod(paymentOptions[0].type);
      return;
    }
    setSelectedPaymentMethod('card');
  }, [paymentOptions, selectedPaymentMethod]);

  useEffect(() => {
    if (!isPaid) {
      return;
    }
    setPaymentIntentError('');
    setPaymentError('');
    setPaymentSuccess("Payment received. We'll email you a receipt shortly.");
    setCustomerSessionClientSecret('');
    setMicrodepositVerificationUrl('');
  }, [isPaid]);

  const createPaymentIntentForSelection = async (paymentMethod) => {
    if (!number || !token) {
      return null;
    }
    const existingMethodConsent =
      (invoice?.autopay_consent?.by_payment_method || {})[paymentMethod] || null;
    const response = await retrievePublicPaymentIntent({
      number,
      token,
      payment_method: paymentMethod,
      save_payment_method:
        Boolean(existingMethodConsent?.authorized) || autopayConsentChecked,
    });
    const data = response?.data || {};
    return {
      clientSecret: data.client_secret || '',
      paymentIntentId: data.payment_intent_id || '',
    };
  };

  const invoiceTotalAmount = useMemo(
    () => parseAmount(invoice?.balance_due ?? invoice?.total_amount),
    [invoice],
  );
  const clientBreakdown = useMemo(() => {
    if (!invoice) {
      return [];
    }
    if (Array.isArray(invoice.client_breakdown)) {
      return invoice.client_breakdown;
    }
    if (Array.isArray(invoice.client_breakdown_snapshot)) {
      return invoice.client_breakdown_snapshot;
    }
    return [];
  }, [invoice]);
  const selectedOption = useMemo(
    () => paymentOptions.find((option) => option.type === selectedPaymentMethod) || paymentOptions[0],
    [paymentOptions, selectedPaymentMethod],
  );

  const billToLines = useMemo(() => {
    if (!invoice) {
      return [];
    }
    const lines = [];
    const clientName = sanitizeLine(invoice.client_name);
    if (clientName) {
      lines.push(clientName);
    }
    if (Array.isArray(invoice.billing_contacts_snapshot)) {
      invoice.billing_contacts_snapshot.forEach((contact) => {
        if (!contact || typeof contact !== 'object') {
          return;
        }
        const name = sanitizeLine(contact.name);
        const email = sanitizeLine(contact.email || contact.address);
        const phone = sanitizeLine(contact.phone);
        const parts = [name, email].filter(Boolean);
        if (parts.length) {
          lines.push(parts.join(' • '));
        } else if (phone) {
          lines.push(phone);
        }
      });
    }
    buildAddressLines(invoice.billing_address_snapshot).forEach((line) => {
      lines.push(line);
    });
    return dedupeLines(lines);
  }, [invoice]);

  const attachments = invoice?.attachments ?? [];

  return (
    <Container maxWidth="md" sx={{ py: { xs: 6, md: 8 } }}>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <Stack spacing={4}>
        <Box textAlign="center">
          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 2 }}>
            Lead Gurus Billing
          </Typography>
          <Typography variant="h3" sx={{ fontWeight: 700, mt: 1 }}>
            Invoice Summary
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 1 }}>
            {invoice
              ? `${invoice.client_name || 'Client'} • ${invoice.period_label || 'Billing period'}`
              : 'Review invoice details and pay securely'}
          </Typography>
        </Box>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" sx={{ py: 8 }}>
            <CircularProgress color="primary" />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            {error}
          </Alert>
        ) : invoice ? (
          <Stack spacing={4}>
            <Card elevation={8} sx={{ borderRadius: 3 }}>
              <CardHeader
                sx={{ pb: 0 }}
                title={
                  <Stack spacing={2}>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={2}
                      alignItems={{ xs: 'flex-start', sm: 'center' }}
                      justifyContent="space-between"
                    >
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Box
                          component="img"
                          src={houseLogo}
                          alt="Lead Gurus logo"
                          sx={{ height: 40, width: 'auto' }}
                        />
                        {invoice.client_logo_file ? (
                          <Box
                            component="img"
                            src={invoice.client_logo_file}
                            alt={`${invoice.client_name || 'Client'} logo`}
                            sx={{
                              height: 40,
                              width: 'auto',
                              maxWidth: { xs: '100%', sm: 160 },
                              objectFit: 'contain',
                            }}
                          />
                        ) : null}
                      </Stack>
                      <Box textAlign={{ xs: 'left', sm: 'right' }}>
                        <Stack spacing={1} alignItems={{ xs: 'flex-start', sm: 'flex-end' }}>
                          <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            Invoice {invoice.number}
                          </Typography>
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {invoice.period_label}
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={handleDownloadPdf}
                          disabled={downloadLoading}
                          sx={{ borderRadius: 9999, textTransform: 'none', mt: 1 }}
                        >
                          {downloadLoading ? 'Downloading…' : 'Download PDF'}
                        </Button>
                      </Box>
                    </Stack>
                  </Stack>
                }
              />
              <CardContent>
                <Stack spacing={4}>
                  {downloadError ? (
                    <Alert severity="error" sx={{ borderRadius: 2 }}>
                      {downloadError}
                    </Alert>
                  ) : null}
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        From
                      </Typography>
                      {fromLines.length ? (
                        <Stack spacing={0.5} sx={{ mt: 1 }}>
                          {fromLines.map((line, index) => (
                            <Typography
                              key={`from-${index}`}
                              variant={index === 0 ? 'body1' : 'body2'}
                              color={index === 0 ? 'text.primary' : 'text.secondary'}
                            >
                              {line}
                            </Typography>
                          ))}
                        </Stack>
                      ) : (
                        <Typography color="text.secondary" sx={{ mt: 1 }}>
                          Company details unavailable.
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Bill to
                      </Typography>
                      {billToLines.length ? (
                        <Stack spacing={0.5} sx={{ mt: 1 }}>
                          {billToLines.map((line, index) => (
                            <Typography
                              key={`bill-to-${index}`}
                              variant={index === 0 ? 'body1' : 'body2'}
                              color={index === 0 ? 'text.primary' : 'text.secondary'}
                            >
                              {line}
                            </Typography>
                          ))}
                        </Stack>
                      ) : (
                        <Typography color="text.secondary" sx={{ mt: 1 }}>
                          Billing contact information unavailable.
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Client
                      </Typography>
                      <Typography variant="h6">{invoice.client_name}</Typography>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 3 }}>
                        Amount due
                      </Typography>
                      <Typography variant="h4" color="primary" sx={{ fontWeight: 700 }}>
                        {formatCurrency(invoice.balance_due ?? invoice.total_amount)}
                      </Typography>
                      {parseFloat(invoice.amount_paid) > 0 && (
                        <Typography variant="body2" color="text.secondary">
                          {formatCurrency(invoice.amount_paid)} paid of{' '}
                          {formatCurrency(invoice.total_amount)} total
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Stack spacing={1.5}>
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            Issue date
                          </Typography>
                          <Typography>{formatDate(invoice.issue_date)}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            Due date
                          </Typography>
                          <Typography>{formatDate(invoice.due_date)}</Typography>
                        </Box>
                      </Stack>
                    </Grid>
                  </Grid>
                </Stack>
                {invoice.external_notes ? (
                  <Alert severity="info" sx={{ mt: 4, borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Client message
                    </Typography>
                    <Typography whiteSpace="pre-line">{invoice.external_notes}</Typography>
                  </Alert>
                ) : null}
              </CardContent>
            </Card>

            <Card elevation={8} sx={{ borderRadius: 3 }}>
              <CardHeader title="Pay securely online" sx={{ pb: 0 }} />
              <CardContent>
                <Stack spacing={3}>
                  {paymentIntentError && !paymentComplete ? (
                    <Alert severity="error" sx={{ borderRadius: 2 }}>
                      {paymentIntentError}
                    </Alert>
                  ) : null}
                  {paymentSubmissionStatus === 'processing' &&
                  !isPaid &&
                  !paymentError &&
                  !paymentIntentError &&
                  !persistedPaymentFailureMessage ? (
                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                      {paymentSuccess ||
                        'Payment submitted. Stripe is processing your payment. We will update this page automatically when a final result is available.'}
                    </Alert>
                  ) : null}
                  {paymentComplete ? (
                    <Alert severity="success" sx={{ borderRadius: 2 }}>
                      {paymentSuccess || "Payment received. We'll email you a receipt shortly."}
                    </Alert>
                  ) : null}
                  {hasMicrodepositVerificationState && !paymentComplete ? (
                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                        One more step to finish this bank payment
                      </Typography>
                      <Typography variant="body2">
                        We sent two small deposits to your account — they usually land within
                        1–2 business days and will show as amounts under $1.00. When you see
                        them, click <strong>Verify bank now</strong> and enter the two amounts.
                        {microdepositVerificationUrl
                          ? ''
                          : ' If the button is not shown yet, click Refresh payment status.'}
                      </Typography>
                    </Alert>
                  ) : null}
                  {hasMicrodepositVerificationState &&
                  microdepositVerificationUrl &&
                  !paymentComplete ? (
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => {
                          window.open(microdepositVerificationUrl, '_blank', 'noopener,noreferrer');
                        }}
                      >
                        Verify bank now
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={paymentStatusRefreshing}
                        onClick={handleRefreshPaymentStatus}
                      >
                        {paymentStatusRefreshing ? 'Refreshing…' : 'Refresh payment status'}
                      </Button>
                    </Stack>
                  ) : null}
                  {hasMicrodepositVerificationState &&
                  !microdepositVerificationUrl &&
                  !paymentComplete ? (
                    <Box display="flex" justifyContent="flex-end">
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={paymentStatusRefreshing}
                        onClick={handleRefreshPaymentStatus}
                      >
                        {paymentStatusRefreshing ? 'Refreshing…' : 'Refresh payment status'}
                      </Button>
                    </Box>
                  ) : null}
                  {paymentError &&
                  !paymentComplete &&
                  !hasMicrodepositVerificationState ? (
                    <Alert severity="error" sx={{ borderRadius: 2 }}>
                      {paymentError}
                    </Alert>
                  ) : null}
                  {persistedPaymentFailureMessage &&
                  !paymentComplete &&
                  !paymentError &&
                  !hasMicrodepositVerificationState &&
                  !paymentSubmitting ? (
                    <Alert severity="error" sx={{ borderRadius: 2 }}>
                      {persistedPaymentFailureMessage}
                    </Alert>
                  ) : null}
                  {selectedPaymentMethod === 'us_bank_account' &&
                  !paymentComplete &&
                  !paymentError &&
                  !paymentIntentError &&
                  !persistedPaymentFailureMessage &&
                  paymentSubmissionStatus !== 'processing' ? (
                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                      {ACH_RECOMMENDED_TEXT}
                      {' '}
                      Manual bank entry may require microdeposit verification (1-2 business days). Bank login is usually faster.
                    </Alert>
                  ) : null}
                  {(paymentIntentError || paymentError || persistedPaymentFailureMessage) &&
                  !paymentComplete &&
                  !hasMicrodepositVerificationState &&
                  paymentSubmissionStatus !== 'processing' ? (
                    <Box display="flex" justifyContent="flex-end">
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          setPaymentIntentError('');
                          setPaymentError('');
                          setPaymentSuccess('');
                          setPaymentSubmissionStatus('');
                          setCustomerSessionClientSecret('');
                          setStripePaymentMethodOptions({});
                          setMicrodepositVerificationUrl('');
                          setAutopayConsentSubmitting(false);
                          setPaymentIntentRefreshNonce((prev) => prev + 1);
                        }}
                      >
                        Try again
                      </Button>
                    </Box>
                  ) : null}
                  {paymentIntentLoading && !paymentComplete ? (
                    <Box display="flex" justifyContent="center" alignItems="center" sx={{ py: 4 }}>
                      <CircularProgress color="primary" size={32} />
                    </Box>
                  ) : null}
                  {stripePromise &&
                  !paymentComplete &&
                  paymentSubmissionStatus !== 'processing' ? (
                    <PaymentForm
                      customerSessionClientSecret={customerSessionClientSecret}
                      stripePromise={stripePromise}
                      currency={stripeCurrency}
                      paymentMethodOptions={stripePaymentMethodOptions}
                      number={number}
                      token={token}
                      invoiceTotal={invoiceTotalAmount}
                      invoiceTotalRaw={invoice?.balance_due ?? invoice?.total_amount}
                      options={paymentOptions}
                      selectedOption={selectedOption}
                      onSelectionChange={(type) => setSelectedPaymentMethod(type)}
                      selectionDisabled={paymentIntentLoading}
                      onSuccess={(message, status) => {
                        if (!message) {
                          setPaymentSubmissionStatus('');
                          setPaymentSuccess('');
                          return;
                        }
                        if (status === 'processing') {
                          setPaymentSubmissionStatus('processing');
                        } else {
                          setPaymentSubmissionStatus('succeeded');
                        }
                        setPaymentSuccess(message);
                        setPaymentIntentError('');
                        setPaymentError('');
                      }}
                      onError={(message) => {
                        setPaymentSubmissionStatus('');
                        setPaymentError(message);
                        if (message) {
                          setPaymentSuccess('');
                        }
                      }}
                      onMicrodepositVerificationUrl={(url) =>
                        setMicrodepositVerificationUrl(url || '')
                      }
                      createPaymentIntent={createPaymentIntentForSelection}
                      autopayConsentChecked={autopayConsentChecked}
                      autopayConsentText={autopayConsentText}
                      autopayConsentSubmitting={autopayConsentSubmitting}
                      autopayAlreadyAuthorized={autopayAlreadyAuthorized}
                      onAutopayConsentChange={(value) => {
                        if (value === 'open_details') {
                          setAutopayConsentDialogOpen(true);
                          return;
                        }
                        setAutopayConsentChecked(Boolean(value));
                      }}
                      onAutopayConsentSaved={(consent) => {
                        setAutopayConsentSubmitting(false);
                        if (!consent) {
                          return;
                        }
                          setInvoice((prev) => (
                            prev
                              ? {
                                  ...prev,
                                  autopay_consent: consent,
                                }
                              : prev
                          ));
                      }}
                      setAutopayConsentSubmitting={setAutopayConsentSubmitting}
                      submitting={paymentSubmitting}
                      setSubmitting={setPaymentSubmitting}
                    />
                  ) : null}
                  {!stripePromise &&
                  !paymentIntentLoading &&
                  !paymentIntentError &&
                  !paymentComplete &&
                  paymentSubmissionStatus !== 'processing' ? (
                    <Stack spacing={1.5} alignItems="flex-start">
                      <Typography color="text.secondary">
                        Payment form unavailable. Please reach out to billing@leadgurus.com for assistance.
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          setPaymentIntentError('');
                          setPaymentError('');
                          setPaymentSuccess('');
                          setPaymentSubmissionStatus('');
                          setCustomerSessionClientSecret('');
                          setStripePaymentMethodOptions({});
                          setMicrodepositVerificationUrl('');
                          setPaymentIntentRefreshNonce((prev) => prev + 1);
                        }}
                      >
                        Retry payment form
                      </Button>
                    </Stack>
                  ) : null}
                </Stack>
              </CardContent>
            </Card>

            {(reportedAt || invoice?.payment_report_expires_on) && !paymentComplete ? (
              <Alert severity="success" sx={{ borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Thanks — we&apos;ll look out for it
                </Typography>
                <Typography variant="body2">
                  We&apos;ve paused payment reminders for this invoice while your
                  payment reaches us. Nothing else is needed from you.
                </Typography>
              </Alert>
            ) : null}

            {/* One report per invoice — after that it's an email to billing. */}
            {!paymentComplete && !reportedAt && !invoice?.payment_reported ? (
              <Button
                variant="outlined"
                size="large"
                fullWidth
                onClick={() => setReportDialogOpen(true)}
                sx={{ borderRadius: 9999, textTransform: 'none', fontWeight: 600, py: 1.25 }}
              >
                Already sent this payment? Let us know
              </Button>
            ) : null}

            <Box display="flex" justifyContent="center" gap={1} flexWrap="wrap">
              <Button
                variant="outlined"
                size="small"
                onClick={() => setAchDialogOpen(true)}
                sx={{ borderRadius: 9999, textTransform: 'none' }}
              >
                Manual ACH details
              </Button>
            </Box>

            <Dialog
              open={reportDialogOpen}
              onClose={() => (reportSubmitting ? null : setReportDialogOpen(false))}
              maxWidth="xs"
              fullWidth
            >
              <DialogTitle>Already sent this payment?</DialogTitle>
              <DialogContent dividers>
                <Stack spacing={2}>
                  <Typography variant="body2" color="text.secondary">
                    Bank transfers and checks take a few days to reach us and
                    don&apos;t show up here straight away. Tell us it&apos;s on
                    the way and we&apos;ll stop the reminders while we wait.
                  </Typography>
                  <TextField
                    select
                    label="How did you pay?"
                    value={reportForm.payment_method}
                    onChange={(event) =>
                      setReportForm((prev) => ({ ...prev, payment_method: event.target.value }))
                    }
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="ach_wire">ACH or wire</MenuItem>
                    <MenuItem value="check">Check</MenuItem>
                    <MenuItem value="other">Something else</MenuItem>
                  </TextField>
                  <TextField
                    type="date"
                    label="Date sent"
                    value={reportForm.sent_on}
                    onChange={(event) =>
                      setReportForm((prev) => ({ ...prev, sent_on: event.target.value }))
                    }
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label="Reference or check number (optional)"
                    value={reportForm.reference}
                    onChange={(event) =>
                      setReportForm((prev) => ({ ...prev, reference: event.target.value }))
                    }
                    fullWidth
                    size="small"
                    helperText="Helps us match it to your payment faster."
                  />
                  {reportError ? <Alert severity="error">{reportError}</Alert> : null}
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setReportDialogOpen(false)} disabled={reportSubmitting}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleReportPayment}
                  disabled={reportSubmitting}
                >
                  {reportSubmitting ? 'Saving…' : 'Let them know'}
                </Button>
              </DialogActions>
            </Dialog>

            <Dialog open={achDialogOpen} onClose={() => setAchDialogOpen(false)} maxWidth="xs" fullWidth>
              <DialogTitle>Manual ACH details</DialogTitle>
              <DialogContent dividers>
                {achLines.length ? (
                  <Stack spacing={1.5}>
                    {achLines.map((line) => (
                      <Typography key={line} fontFamily="'Roboto Mono', monospace" fontSize={13}>
                        {line}
                      </Typography>
                    ))}
                  </Stack>
                ) : (
                  <Typography color="text.secondary" variant="body2">
                    ACH instructions will be provided upon request.
                  </Typography>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setAchDialogOpen(false)} sx={{ textTransform: 'none' }}>
                  Close
                </Button>
              </DialogActions>
            </Dialog>

            <Dialog
              open={autopayConsentDialogOpen}
              onClose={() => setAutopayConsentDialogOpen(false)}
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle>Future Auto-Charge Authorization</DialogTitle>
              <DialogContent dividers>
                <Stack spacing={2}>
                  <Typography variant="body2">
                    {autopayConsentText}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    By opting in, you authorize Lead Gurus to keep this payment method on file
                    and use it for future invoices for this billing account until you revoke
                    that authorization in writing.
                  </Typography>
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => setAutopayConsentDialogOpen(false)}
                  sx={{ textTransform: 'none' }}
                >
                  Close
                </Button>
              </DialogActions>
            </Dialog>

            <Card elevation={3} sx={{ borderRadius: 3 }}>
              <CardHeader title="Invoice items" sx={{ pb: 0 }} />
              <CardContent>
                {invoice.line_items?.length ? (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Description</TableCell>
                        <TableCell align="right">Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invoice.line_items.map((item) => (
                        <TableRow key={`${item.description}-${item.line_total}`}>
                          <TableCell>
                            <Typography fontWeight={600}>{item.display_description || item.description}</Typography>
                            {item.fee_percentage ? (
                              <Typography variant="caption" color="text.secondary">
                                Fee {item.fee_percentage}%
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell align="right">{formatCurrency(item.line_total)}</TableCell>
                        </TableRow>
                      ))}
                      {parseFloat(invoice.amount_paid) > 0 && (
                        <TableRow>
                          <TableCell>Invoice total</TableCell>
                          <TableCell align="right">
                            {formatCurrency(invoice.total_amount)}
                          </TableCell>
                        </TableRow>
                      )}
                      {parseFloat(invoice.amount_paid) > 0 && (
                        <TableRow>
                          <TableCell>Paid</TableCell>
                          <TableCell align="right" sx={{ color: 'success.main' }}>
                            −{formatCurrency(invoice.amount_paid)}
                          </TableCell>
                        </TableRow>
                      )}
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>
                          {parseFloat(invoice.amount_paid) > 0 ? 'Remaining balance' : 'Total due'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          {formatCurrency(invoice.balance_due ?? invoice.total_amount)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                ) : (
                  <Typography color="text.secondary">No line items available.</Typography>
                )}
              </CardContent>
            </Card>

            {invoice.territory_breakdown?.length ? (
              <Card elevation={3} sx={{ borderRadius: 3 }}>
                <CardHeader
                  title="Territory & Vertical Breakdown"
                  sx={{ pb: 0 }}
                  action={
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleDownloadTerritoryCsv}
                      sx={{ borderRadius: 9999, textTransform: 'none' }}
                    >
                      Download CSV
                    </Button>
                  }
                />
                <CardContent>
                  {invoice.territory_breakdown_mode === 'fee_only' ? (
                    <>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Your ad spend is billed to you directly by the ad platforms and is shown here
                        for reference only. This invoice is the Lead Gurus fee on that spend.
                      </Typography>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Territory</TableCell>
                            <TableCell>Vertical</TableCell>
                            <TableCell align="right">Ad spend (paid directly by you)</TableCell>
                            <TableCell align="right">
                              Lead Gurus fee
                              {invoice.fee_percentage ? ` (${Number(invoice.fee_percentage)}%)` : ''}
                            </TableCell>
                            <TableCell align="right">Total</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {invoice.territory_breakdown.map((row) => (
                            <TableRow key={`${row.territory}-${row.territory_id || 'na'}`}>
                              <TableCell>{row.territory}</TableCell>
                              <TableCell>{row.vertical || ''}</TableCell>
                              <TableCell align="right" sx={{ color: 'text.secondary' }}>
                                {row.base_spend != null ? formatCurrency(row.base_spend) : ''}
                              </TableCell>
                              <TableCell align="right">{formatCurrency(row.spend)}</TableCell>
                              <TableCell align="right">
                                {formatCurrency(row.total_cost ?? row.spend)}
                              </TableCell>
                            </TableRow>
                          ))}
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700 }} colSpan={2}>
                              Total
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                              {formatCurrency(invoice.territory_ad_spend_total)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                              {formatCurrency(invoice.territory_breakdown_total)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                              {formatCurrency(invoice.territory_total_cost)}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </>
                  ) : (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Territory</TableCell>
                          <TableCell>Vertical</TableCell>
                          <TableCell align="right">Spend</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {invoice.territory_breakdown.map((row) => (
                          <TableRow key={`${row.territory}-${row.territory_id || 'na'}`}>
                            <TableCell>{row.territory}</TableCell>
                            <TableCell>{row.vertical || 'Unassigned'}</TableCell>
                            <TableCell align="right">{formatCurrency(row.spend)}</TableCell>
                          </TableRow>
                        ))}
                        {invoice.territory_breakdown_total ? (
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700 }} colSpan={2}>
                              Total
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                              {formatCurrency(invoice.territory_breakdown_total)}
                            </TableCell>
                          </TableRow>
                        ) : null}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            ) : null}

            {clientBreakdown.length ? (
              <Card elevation={3} sx={{ borderRadius: 3 }}>
                <CardHeader
                  title="Client Breakdown"
                  sx={{ pb: 0 }}
                  action={
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleDownloadClientBreakdownCsv}
                      sx={{ borderRadius: 9999, textTransform: 'none' }}
                    >
                      Download CSV
                    </Button>
                  }
                />
                <CardContent>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Client</TableCell>
                        <TableCell align="right">Spend</TableCell>
                        <TableCell align="right">Fee</TableCell>
                        <TableCell align="right">Line total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {clientBreakdown.map((row, index) => (
                        <TableRow key={`${row.client_id || index}-${row.client_name || 'client'}`}>
                          <TableCell>{row.client_name || '—'}</TableCell>
                          <TableCell align="right">{formatCurrency(row.spend_amount)}</TableCell>
                          <TableCell align="right">{formatCurrency(row.fee_amount)}</TableCell>
                          <TableCell align="right">{formatCurrency(row.line_total)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }} colSpan={3}>
                          Total
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          {formatCurrency(invoice.total_amount)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            ) : null}

            <Card elevation={3} sx={{ borderRadius: 3 }}>
              <CardHeader title="Attachments" sx={{ pb: 0 }} />
              <CardContent>
                {attachments.length ? (
                  <Stack spacing={2}>
                    {attachments.map((attachment) => {
                      const key = attachment.id || attachment.download_url || attachment.filename;
                      const uploadedDate = formatDate(attachment.uploaded_at);
                      const uploadLabel = uploadedDate !== '—' ? `Uploaded ${uploadedDate}` : null;
                      const detailParts = [attachment.notes, uploadLabel].filter(Boolean);
                      return (
                        <Box key={key}>
                          <Typography
                            component="a"
                            href={attachment.download_url || attachment.file}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ fontWeight: 600, display: 'inline-block' }}
                          >
                            {attachment.filename || attachment.original_filename || 'Attachment'}
                          </Typography>
                          {detailParts.length ? (
                            <Typography variant="body2" color="text.secondary">
                              {detailParts.join(' • ')}
                            </Typography>
                          ) : null}
                        </Box>
                      );
                    })}
                  </Stack>
                ) : (
                  <Typography color="text.secondary">No attachments included.</Typography>
                )}
              </CardContent>
            </Card>

            <Divider />
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Need help? Email <a href="mailto:billing@leadgurus.com">billing@leadgurus.com</a> and include
              your invoice number {invoice.number}.
            </Typography>
          </Stack>
        ) : null}
      </Stack>
    </Container>
  );
}
